import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Modal, Button, Icon } from 'antd'
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'
import 'antd/dist/antd.css'

import './ImagePreview.scss'

/**
 * Normalise an images entry into { src, alt }.
 * Accepts a plain URL string or an object with src/alt.
 */
const normalizeImage = (img, fallbackAlt) => {
  if (typeof img === 'string')
    return { src: img, alt: fallbackAlt || 'Preview' }
  return { src: img.src, alt: img.alt || fallbackAlt || 'Preview' }
}

// Padding to keep image from touching viewport edges
const VIEWPORT_PADDING = 40

const ImagePreview = props => {
  const [visible, setVisible] = useState(false)
  const [rotation, setRotation] = useState(0)
  const [initialScale, setInitialScale] = useState(1)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isFading, setIsFading] = useState(false)
  const contentRef = useRef(null)
  const fadeTimerRef = useRef(null)

  // Build the images list — prefer explicit `images` array, fall back to single `src`
  const imagesList = React.useMemo(() => {
    if (Array.isArray(props.images) && props.images.length > 0) {
      return props.images.map(img => normalizeImage(img, props.alt))
    }
    if (props.src) {
      return [{ src: props.src, alt: props.alt || 'Preview' }]
    }
    return []
  }, [props.images, props.src, props.alt])

  const isCarousel = imagesList.length > 1

  const showModal = () => {
    const startIndex =
      typeof props.initialIndex === 'number'
        ? Math.max(0, Math.min(props.initialIndex, imagesList.length - 1))
        : 0
    setCurrentIndex(startIndex)
    setRotation(0)
    setIsFading(false)
    setVisible(true)
  }

  const handleCancel = () => {
    setVisible(false)
    setRotation(0)
    setIsFading(false)
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current)
  }

  const rotateClockwise = () => {
    setRotation(prevRotation => prevRotation + 90)
  }

  const rotateCounterclockwise = () => {
    setRotation(prevRotation => prevRotation - 90)
  }

  const resetRotation = () => {
    setRotation(0)
  }

  // Smooth crossfade navigation
  const navigateTo = useCallback(
    newIndex => {
      if (newIndex === currentIndex) return
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current)

      // Phase 1: fade out
      setIsFading(true)

      fadeTimerRef.current = setTimeout(() => {
        // Phase 2: swap image (while invisible)
        setCurrentIndex(newIndex)
        setRotation(0)

        // Phase 3: fade back in after a tiny delay for the new image to mount
        fadeTimerRef.current = setTimeout(() => {
          setIsFading(false)
        }, 50)
      }, 200) // matches the CSS transition duration
    },
    [currentIndex]
  )

  const goPrev = useCallback(() => {
    const newIndex =
      currentIndex === 0 ? imagesList.length - 1 : currentIndex - 1
    navigateTo(newIndex)
  }, [currentIndex, imagesList.length, navigateTo])

  const goNext = useCallback(() => {
    const newIndex =
      currentIndex === imagesList.length - 1 ? 0 : currentIndex + 1
    navigateTo(newIndex)
  }, [currentIndex, imagesList.length, navigateTo])

  // Responsive image sizing — fits within viewport, centered
  const handleImageLoad = (e, { setTransform }) => {
    const img = e.target
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // Calculate scale to fit image (object-fit: contain behavior)
    const scaleX = viewportWidth / img.naturalWidth
    const scaleY = viewportHeight / img.naturalHeight
    const fitScale = Math.min(scaleX, scaleY, 1) // Don't scale up beyond natural size if it already fits

    setInitialScale(fitScale)

    // Center the image initially with the calculated scale
    const x = (viewportWidth - img.naturalWidth * fitScale) / 2
    const y = (viewportHeight - img.naturalHeight * fitScale) / 2
    setTransform(x, y, fitScale, 0)
  }

  // Keyboard navigation
  useEffect(() => {
    if (!visible || !isCarousel) return

    const handleKeyDown = e => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goPrev()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        goNext()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [visible, isCarousel, goPrev, goNext])

  // Cleanup fade timer on unmount
  useEffect(() => {
    return () => {
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current)
    }
  }, [])

  const activeImage = imagesList[currentIndex] || imagesList[0] || {}

  return (
    <>
      <img
        key={props.src}
        alt={props.alt || 'Preview'}
        src={props.src}
        crossOrigin={props.crossorigin}
        onClick={showModal}
        className={props.className ? props.className : 'shl-image-preview'}
        style={{
          cursor: 'pointer',
          maxWidth: '100%',
          opacity: props.opacity ? props.opacity : 1,
          ...props.style
        }}
      />

      <Modal
        visible={visible}
        onCancel={handleCancel}
        footer={null}
        width="100vw"
        style={{
          maxWidth: '100vw',
          margin: 0,
          top: 0,
          padding: 0
        }}
        bodyStyle={{
          height: '100vh',
          padding: 0
        }}
        wrapClassName="shl-image-preview-modal"
        destroyOnClose
      >
        <TransformWrapper
          key={currentIndex}
          initialScale={initialScale}
          minScale={0.05}
          maxScale={10}
          centerOnInit={true}
          limitToBounds={true}
          wheel={{ step: 0.1 }}
          pinch={{ step: 0.1 }}
          doubleClick={{ disabled: true }}
          alignmentAnimation={{ disabled: true }}
        >
          {transformProps => {
            const {
              zoomIn,
              zoomOut,
              resetTransform,
              setTransform
            } = transformProps
            return (
              <div className="image-preview-container">
                {/* Carousel navigation arrows */}
                {isCarousel && (
                  <>
                    <button
                      className="carousel-nav carousel-nav-prev"
                      onClick={e => {
                        e.stopPropagation()
                        goPrev()
                      }}
                      aria-label="Previous image"
                    >
                      <Icon type="left" />
                    </button>
                    <button
                      className="carousel-nav carousel-nav-next"
                      onClick={e => {
                        e.stopPropagation()
                        goNext()
                      }}
                      aria-label="Next image"
                    >
                      <Icon type="right" />
                    </button>
                  </>
                )}

                {/* Image info: title + counter at top-left */}
                {isCarousel && (
                  <div className="carousel-info">
                    {activeImage.alt && (
                      <span className="carousel-title">{activeImage.alt}</span>
                    )}
                    <span className="carousel-counter">
                      {currentIndex + 1} / {imagesList.length}
                    </span>
                  </div>
                )}

                <div className="shl-tools">
                  <Button onClick={() => zoomIn()}>
                    <Icon type="zoom-in" />
                  </Button>
                  <Button onClick={() => zoomOut()}>
                    <Icon type="zoom-out" />
                  </Button>
                  <Button onClick={rotateClockwise}>
                    <Icon type="redo" />
                  </Button>
                  <Button onClick={rotateCounterclockwise}>
                    <Icon type="undo" />
                  </Button>
                  <Button
                    className="btn-reset"
                    onClick={() => {
                      resetRotation()
                      resetTransform()
                    }}
                  >
                    Reset
                  </Button>
                </div>

                <TransformComponent
                  wrapperStyle={{
                    width: '100vw',
                    height: '100vh'
                  }}
                  contentStyle={{
                    width: 'auto',
                    height: 'auto',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <div
                    ref={contentRef}
                    className={`image-wrapper ${isFading ? 'is-fading' : ''}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      transformOrigin: 'center center'
                    }}
                  >
                    <img
                      alt={activeImage.alt || 'Preview'}
                      src={activeImage.src}
                      crossOrigin={props.crossorigin}
                      onLoad={e => handleImageLoad(e, transformProps)}
                      style={{
                        maxWidth: 'none',
                        maxHeight: 'none',
                        transform: `rotate(${rotation}deg)`,
                        transformOrigin: 'center center',
                        userSelect: 'none',
                        WebkitUserDrag: 'none'
                      }}
                    />
                  </div>
                </TransformComponent>
              </div>
            )
          }}
        </TransformWrapper>
      </Modal>
    </>
  )
}

export default ImagePreview
